import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { IEditTable, ITable } from '../../interfaces/table';
import {
  AnyIntegration,
  AnyIntegrationNew,
  CreateRestaurantComment,
  CreateRestaurantModem,
  IFeatureCallWaiter,
  IRestaurantModem,
  IRestaurantInfo,
  RestaurantComment,
  RestaurantCommentSection,
  UpdateRestaurantModem,
} from '../../interfaces/restaurant';
import { Period, NewPeriod, RestaurantWorkPeriod, NewRestaurantWorkPeriod } from '../../interfaces/period';

@Injectable({
  providedIn: 'root'
})
export class RestaurantService {

  constructor(
    private http: HttpClient
  ) {
  }

  public getComments(
    restaurantId: number,
    section: RestaurantCommentSection = 'admin'
  ): Observable<RestaurantComment[]> {
    return this.http.get<RestaurantComment[]>(`/restaurants/${restaurantId}/comments`, {
      params: { section }
    });
  }

  public createComment(
    restaurantId: number,
    comment: CreateRestaurantComment
  ): Observable<RestaurantComment> {
    return this.http.post<RestaurantComment>(`/restaurants/${restaurantId}/comments`, comment);
  }

  public getRestaurantModems(restaurantId: number): Observable<IRestaurantModem[]> {
    return this.http.get<IRestaurantModem[]>(`/restaurants/${restaurantId}/modems`);
  }

  public getRestaurantModem(restaurantId: number, modemId: number): Observable<IRestaurantModem> {
    return this.http.get<IRestaurantModem>(`/restaurants/${restaurantId}/modems/${modemId}`);
  }

  public createRestaurantModem(
    restaurantId: number,
    payload: CreateRestaurantModem
  ): Observable<IRestaurantModem> {
    return this.http.post<IRestaurantModem>(`/restaurants/${restaurantId}/modems`, payload);
  }

  public updateRestaurantModem(
    restaurantId: number,
    modemId: number,
    payload: UpdateRestaurantModem
  ): Observable<IRestaurantModem> {
    return this.http.patch<IRestaurantModem>(`/restaurants/${restaurantId}/modems/${modemId}`, payload);
  }

  public deleteRestaurantModem(restaurantId: number, modemId: number): Observable<void> {
    return this.http.delete<void>(`/restaurants/${restaurantId}/modems/${modemId}`);
  }

  public addTable(id: number, table: {
    name: string,
    number?: number,
    widgetCode?: string,
    dwallDeviceId?: string,
    description?: string,
    qrCodeFileId?: string,
  }): Observable<ITable> {
    return this.http.post<ITable>(`/restaurants/${id}/tables`, table);
  }

  public linkNewDeviceToTable(restaurantId: number, tableId: number, payload: {
    widgetCode?: string,
    dwallDeviceId?: string,
  }): Observable<ITable> {
    return this.http.post<ITable>(`/restaurants/${restaurantId}/tables/${tableId}/link-device`, payload);
  }

  public unlinkDevice(restaurantId: number, tableId: number): Observable<ITable> {
    return this.http.post<ITable>(`/restaurants/${restaurantId}/tables/${tableId}/unlink-device`, {});
  }

  public editTable(restaurantId: number, tableId: number, table: {
    name?: string,
    number?: number | null,
    qrCodeFileId?: string,
    description?: string,
    dwallDeviceId?: string | null
  }): Observable<ITable> {
    return this.http.patch<ITable>(`/restaurants/${restaurantId}/tables/${tableId}`, table)
      .pipe(
        catchError(error => {
          return throwError(error);
        })
      );
  }

  public getQrCode(file: any, tableId: number, restaurantId: number): Observable<any> {
    const dataQr: FormData = new FormData();
    dataQr.append('QRCodeImage', file, file.name);
    return this.http.patch(`/restaurants/${restaurantId}/tables/${tableId}`, dataQr);
  }

  public addWaiter({id, name, email, password, tableIds, role = 'WAITER'}:
    { id: string | number, name: string, email: string, password: string, tableIds?: number[],  role?: string }): Observable<any> {
    return this.http.post(`/restaurants/${id}/staff`, {name, email, password, tableIds,  role});
  }

  public getAllTables(id: number): Observable<any> {
    return this.http.get(`/restaurants/${id}/tables`)
      .pipe(
        catchError((err) => {
          throw err;
          return [];
        })
      );
  }

  public getAllWaiters(id: number): Observable<any> {
    return this.http.get(`/restaurants/${id}/staff/waiters/`);
  }

  public removeWaiter(restaurantId: number, staffId: number): Observable<void> {
    return this.http.delete<void>(`/restaurants/${restaurantId}/staff/${staffId}`);
  }

  public getWaiterById(id: number, staffId: number): Observable<any> {
    return this.http.get(`/restaurants/${id}/staff/${staffId}`);
  }

  public editWaiter({id, name, email, password, tableIdsToAttach, tableIdsToDetach, idStaff, role = 'WAITER'}: IEditTable): Observable<any>{
    return this.http.patch(`/restaurants/${id}/staff`, {
      id: idStaff,
      name,
      email,
      password,
      tableIdsToAttach,
      tableIdsToDetach,
      role
    });
  }

  public addIntegration(id: number, {provider, credentials, proxy, proxyUnifiedMode}: AnyIntegrationNew): Observable<any> {
    return this.http.post(`/restaurants/${id}/integrations`, {provider, credentials, proxy, proxyUnifiedMode});
  }

  public getIntegrationById(id: number, integrationId: string): Observable<any> {
    return this.http.get(`/restaurants/${id}/integrations/${integrationId}`);
  }

  public getAllIntegration(id: number): Observable<any> {
    return this.http.get(`/restaurants/${id}/integrations`);
  }

  public editIntegration(restaurantId: number, {id, provider, credentials, proxy, proxyUnifiedMode}: AnyIntegration): Observable<any> {
    return this.http.patch(`/restaurants/${restaurantId}/integrations/${id}`, {
      provider, credentials, proxy, proxyUnifiedMode
    });
  }
  public removeIntegration(id: number, integrationId: string): Observable<void>{
    return this.http.delete<void>(`/restaurants/${id}/integrations/${integrationId}`);
  }

  public configureCollWaiter(restaurantId: number, callWaiter: IFeatureCallWaiter): Observable<IRestaurantInfo> {
    return this.http.patch<IRestaurantInfo>(`/restaurants/${restaurantId}`, {
        callWaiter
    });
  }

  public getBrightnessSettings(restaurantId: number): Observable<any> {
    return this.http.get(`/restaurants/${ restaurantId }/devices-brightness/settings`);
  }

  public configureBrightnessLevel(restaurantId: number, tablesBrightnessLevel: number): Observable<any> {
    return this.http.patch(`/restaurants/${restaurantId}/devices-brightness/settings`, {tablesBrightnessLevel});
  }

  public configureBrightnessControl(restaurantId: number, tablesBrightnessControl: boolean): Observable<any> {
    return this.http.patch(`/restaurants/${restaurantId}/devices-brightness/settings`, {tablesBrightnessControl});
  }

  public configureBrightnessSchedule(restaurantId: number, tablesBrightnessSchedule: boolean): Observable<any> {
    return this.http.patch(`/restaurants/${restaurantId}/devices-brightness/settings`, {tablesBrightnessSchedule});
  }

  public uploadPdf(file: any, id: number): Observable<any> {
    const testData: FormData = new FormData();
    testData.append('menu', file, file.name);
    return this.http.post(`/restaurants/menu/upload/${id}`, testData, {
      reportProgress: true
    });
  }

  public getAllHappyHoursPeriods(id: number): Observable<Period[]> {
    return this.http.get<Period[]>(`/restaurants/${id}/happy-menu-periods`);
  }

  public createHappyHoursPeriod(id: number, period: NewPeriod): Observable<any> {
    return this.http.post(`/restaurants/${id}/happy-menu-periods`, {
      startDay: period.startDay,
      startTime: period.startTime,
      endDay: period.endDay,
      endTime: period.endTime
    });
  }

  public updateHappyHoursPeriod(restaurantId: number, id: number, period: Period): Observable<any> {
    return this.http.patch(`/restaurants/${restaurantId}/happy-menu-periods/${id}`, {
      startDay: period.startDay,
      startTime: period.startTime,
      endDay: period.endDay,
      endTime: period.endTime
    });
  }

  public deleteHappyHoursPeriod(restaurantId: number, id: number): Observable<any> {
    return this.http.delete(`/restaurants/${restaurantId}/happy-menu-periods/${id}`);
  }

  public getWorkingHours(id: number): Observable<RestaurantWorkPeriod[]> {
    return this.http.get<RestaurantWorkPeriod[]>(`/restaurants/${id}/schedule-periods`);
  }

  public createWorkingHoursPeriod(id: number, period: NewRestaurantWorkPeriod): Observable<any> {
    return this.http.post(`/restaurants/${id}/schedule-periods`, {
      active: true,
      startDay: period.startDay,
      startTime: period.startTime,
      devicesAutoOn: period.devicesAutoOn,
      devicesAutoOnOffset: period.devicesAutoOnOffset,
      endDay: period.endDay,
      endTime: period.endTime,
      devicesAutoOff: period.devicesAutoOff,
      devicesAutoOffOffset: period.devicesAutoOffOffset,
    });
  }

  public updateWorkingHoursPeriod(restaurantId: number, period: RestaurantWorkPeriod): Observable<any> {
    return this.http.patch(`/restaurants/${restaurantId}/schedule-periods/${period.id}`, {
      active: true,
      startDay: period.startDay,
      startTime: period.startTime,
      devicesAutoOn: period.devicesAutoOn,
      devicesAutoOnOffset: period.devicesAutoOnOffset,
      endDay: period.endDay,
      endTime: period.endTime,
      devicesAutoOff: period.devicesAutoOff,
      devicesAutoOffOffset: period.devicesAutoOffOffset
    });
  }

  public deleteWorkingHours(restaurantId: number, id: number): Observable<any> {
    return this.http.delete(`/restaurants/${restaurantId}/schedule-periods/${id}`);
  }

  public uploadHappyHoursPdf(file: any, id: number): Observable<any> {
    const data: FormData = new FormData();
    data.append('menu', file, file.name);
    return this.http.post(`/restaurants/happyMenu/upload/${id}`, data);
  }

  public updateClickableMediaConfig(id: number, items: any[]): Observable<any> {
    return this.http.patch(`/restaurants/${id}`, {
      clickableMediaConfig: JSON.stringify(items),
    });
  }

  public getClickableMediaConfig(id: number): Observable<any> {
    return this.http.get(`/restaurants/${id}/clickableMediaConfig`);
  }

  public getAllBrightnessPeriod(restaurantId: number): Observable<Period[]> {
    return this.http.get<Period[]>(`/restaurants/${restaurantId}/devices-brightness/schedule-periods`);
  }

  public createBrightnessPeriod(id: number, period: NewPeriod): Observable<any> {
    return this.http.post(`/restaurants/${id}/devices-brightness/schedule-periods`, {
      active: true,
      level: period.level,
      startDay: period.startDay,
      startTime: period.startTime,
      endDay: period.endDay,
      endTime: period.endTime
    });
  }

  public updateBrightnessPeriod(restaurantId: number, period: Period): Observable<any> {
    return this.http.patch(`/restaurants/${restaurantId}/devices-brightness/schedule-periods/${period.id}`, {
      active: true,
      level: period.level,
      startDay: period.startDay,
      startTime: period.startTime,
      endDay: period.endDay,
      endTime: period.endTime
    });
  }

  public deleteBrightnessPeriod(restaurantId: number, id: number): Observable<any> {
    return this.http.delete(`/restaurants/${restaurantId}/devices-brightness/schedule-periods/${id}`);
  }
}
